import { useCallback, useEffect, useState } from 'react';

import { useApi } from '../api-context';
import type { AssistDecision, AssistProgress } from '../../api';

/**
 * One shoot's Review Assist: what it was told to do, whether it is running, and
 * how far it has got.
 *
 * The run itself belongs to the api layer, not to this hook. A review takes
 * minutes, and the photographer is expected to go and cull while it works — so
 * unmounting this hook must not stop anything. All it does is subscribe.
 */
export function useReviewAssist(projectId: number) {
  const api = useApi();

  const [decision, setDecision] = useState<AssistDecision>('unasked');
  const [progress, setProgress] = useState<AssistProgress | null>(null);
  const [running, setRunning] = useState(false);
  const [isDeep, setIsDeep] = useState(false);

  useEffect(() => {
    const live = api.assistRun(projectId);
    setDecision(api.assistDecision(projectId));
    setProgress(live ?? api.assistProgress(projectId));
    setRunning(live !== null);
    setIsDeep(api.assistIsDeep(projectId));

    return api.onAssistProgress(projectId, (next) => {
      setProgress(next);
      // Ask the registry rather than reading the phase. A shallow pass being
      // cancelled to make way for a deep one reports 'cancelled', and treating
      // that as the end would flash the button back mid-handover.
      setRunning(api.assistRun(projectId) !== null);
      if (next.phase === 'done' || next.phase === 'cancelled') {
        setIsDeep(api.assistIsDeep(projectId));
      }
    });
  }, [api, projectId]);

  const start = useCallback(() => {
    setRunning(true);
    void api.startAssist(projectId, true).finally(() => {
      setRunning(api.assistRun(projectId) !== null);
      setIsDeep(api.assistIsDeep(projectId));
    });
  }, [api, projectId]);

  const stop = useCallback(() => api.stopAssist(projectId), [api, projectId]);

  /** Answer the popup. "Review them" also starts the run. */
  const answer = useCallback(
    (choice: 'deep' | 'declined', always: boolean) => {
      if (always && choice === 'deep') void api.setAssistAlways(true);
      void api.setAssistDecision(projectId, choice);
      setDecision(choice);
      if (choice === 'deep') start();
    },
    [api, projectId, start],
  );

  return { decision, progress, running, isDeep, start, stop, answer };
}
